import React, { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Star, MessageSquareReply, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { author: "", source: "JohnnyMart", rating: 5, text: "", title: "", location: "", featured: false, status: "approved", review_type: "store", product_id: "", verified: false, sort_order: 0 };
const STATUSES = ["pending", "approved", "rejected", "hidden", "flagged", "archived"];
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-red-100 text-red-700",
  hidden: "bg-navy/10 text-navy/60",
  flagged: "bg-orange-100 text-orange-700",
  archived: "bg-navy/10 text-navy/50",
};

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [respondingId, setRespondingId] = useState(null);
  const [responseText, setResponseText] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");

  const load = () => api.get("/reviews", { params: { include_inactive: true } }).then((r) => setReviews(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r, product_id: r.product_id || "" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, rating: Number(form.rating), sort_order: Number(form.sort_order) || 0 };
    if (!payload.product_id) delete payload.product_id;
    try {
      if (editing) await api.put(`/admin/reviews/${editing.id}`, payload);
      else await api.post("/admin/reviews", payload);
      toast.success(editing ? "Review updated" : "Review added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (r, status) => {
    try {
      await api.patch(`/admin/reviews/${r.id}/status`, { status });
      toast.success(`Marked ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete review from "${r.author}"?`)) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const submitResponse = async (r) => {
    try {
      await api.post(`/admin/reviews/${r.id}/respond`, { owner_response: responseText });
      toast.success("Response saved");
      setRespondingId(null);
      setResponseText("");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggleFeatured = async (r) => {
    try {
      await api.put(`/admin/reviews/${r.id}`, { featured: !r.featured });
      toast.success(r.featured ? "Unfeatured" : "Featured");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const counts = STATUSES.reduce((acc, s) => { acc[s] = reviews.filter((r) => r.status === s).length; return acc; }, {});
  let visible = filter === "all" ? reviews : reviews.filter((r) => r.status === filter);
  if (query.trim()) {
    const q = query.trim().toLowerCase();
    visible = visible.filter((r) =>
      (r.author || "").toLowerCase().includes(q) ||
      (r.text || "").toLowerCase().includes(q) ||
      (r.order_number || "").toLowerCase().includes(q));
  }
  visible = [...visible].sort((a, b) => {
    if (sort === "newest") return new Date(b.created_at) - new Date(a.created_at);
    if (sort === "oldest") return new Date(a.created_at) - new Date(b.created_at);
    if (sort === "highest") return b.rating - a.rating;
    if (sort === "lowest") return a.rating - b.rating;
    return 0;
  });

  return (
    <div data-testid="admin-reviews">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl text-navy">Reviews</h1>
          <p className="text-ink/60 mt-1">Moderate customer reviews. Only <b>approved</b> reviews appear publicly.</p>
        </div>
        <button data-testid="add-review-btn" onClick={openCreate} className="inline-flex items-center gap-2 bg-brick text-white font-bold uppercase rounded-md px-5 py-3 hover:bg-navy transition-colors">
          <Plus className="w-4 h-4" /> Add Review
        </button>
      </div>

      <div className="mt-5 flex gap-2 flex-wrap">
        <button data-testid="review-filter-all" onClick={() => setFilter("all")} className={`rounded-full px-3 py-1.5 text-sm font-bold ${filter === "all" ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy"}`}>All ({reviews.length})</button>
        {STATUSES.map((s) => (
          <button key={s} data-testid={`review-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full px-3 py-1.5 text-sm font-bold capitalize ${filter === s ? "bg-brick text-white" : "bg-white border border-navy/20 text-navy"}`}>{s} ({counts[s]})</button>
        ))}
      </div>

      <div className="mt-3 flex flex-col sm:flex-row gap-2">
        <input data-testid="review-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, text or order #..." className="flex-1 rounded-md border border-navy/20 px-3 py-2.5 text-base outline-none focus:border-brick" />
        <select data-testid="review-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="rounded-md border border-navy/20 px-3 py-2.5 bg-white outline-none focus:border-brick">
          <option value="newest">Newest first</option>
          <option value="oldest">Oldest first</option>
          <option value="highest">Highest rating</option>
          <option value="lowest">Lowest rating</option>
        </select>
      </div>

      {visible.length === 0 && (
        <div data-testid="reviews-empty-admin" className="mt-8 text-center bg-white rounded-lg border border-navy/15 p-10 text-navy/50">No reviews match this view.</div>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        {visible.map((r) => (
          <div key={r.id} data-testid={`admin-review-${r.id}`} className="bg-white rounded-lg border border-navy/15 p-5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex gap-0.5 text-gold">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className={`w-4 h-4 ${i < r.rating ? "fill-gold" : "fill-none text-navy/20"}`} />
                  ))}
                </div>
                <span data-testid={`review-status-${r.id}`} className={`text-xs font-bold rounded-full px-2 py-1 capitalize ${STATUS_COLORS[r.status] || "bg-navy/10 text-navy"}`}>{r.status}</span>
                {r.verified && <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-1 bg-green-50 text-green-700"><ShieldCheck className="w-3 h-3" /> Verified</span>}
                {r.featured && <span className="inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2 py-1 bg-gold/20 text-navy"><Star className="w-3 h-3 fill-gold text-gold" /> Featured</span>}
                <span className="text-[11px] font-bold rounded-full px-2 py-1 bg-navy/10 text-navy capitalize">{r.review_type}</span>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`edit-review-${r.id}`} onClick={() => openEdit(r)} className="p-2 text-navy hover:text-brick"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-review-${r.id}`} onClick={() => remove(r)} className="p-2 text-navy hover:text-brick"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {r.title && <p className="mt-2 font-bold text-navy text-sm">{r.title}</p>}
            <p className="mt-1 text-ink/80 text-sm">"{r.text}"</p>
            {r.edited && r.original_text && (
              <details className="mt-1">
                <summary className="text-xs text-ink/50 cursor-pointer">Edited — view original</summary>
                <p className="mt-1 text-xs text-ink/50 italic">Original: "{r.original_text}"</p>
              </details>
            )}
            {r.product_name && <p className="mt-1 text-xs text-brick font-bold">On: {r.product_name}</p>}
            <div className="mt-2 flex items-center justify-between text-sm">
              <span className="font-bold text-navy">{r.author}</span>
              <span className="text-xs text-ink/50">{r.order_number ? `#${r.order_number} · ` : ""}{r.created_at ? new Date(r.created_at).toLocaleDateString() : ""}</span>
            </div>

            {r.owner_response && (
              <div className="mt-2 rounded bg-cream border border-gold/40 p-2 text-xs text-ink/80">
                <b className="text-navy">Your reply:</b> {r.owner_response}
              </div>
            )}

            {/* Status-aware actions only */}
            <div className="mt-3 flex gap-1.5 flex-wrap">
              {r.status !== "approved" && (
                <button data-testid={`approve-${r.id}`} onClick={() => setStatus(r, "approved")} className="text-xs font-bold rounded px-2.5 py-1.5 bg-green-100 text-green-700 hover:bg-green-200">Approve</button>
              )}
              {r.status !== "rejected" && r.status !== "archived" && (
                <button data-testid={`reject-${r.id}`} onClick={() => setStatus(r, "rejected")} className="text-xs font-bold rounded px-2.5 py-1.5 bg-red-100 text-red-700 hover:bg-red-200">Reject</button>
              )}
              {r.status !== "archived" && (
                <button data-testid={`archive-${r.id}`} onClick={() => setStatus(r, "archived")} className="text-xs font-bold rounded px-2.5 py-1.5 bg-navy/10 text-navy/70 hover:bg-navy/20">Archive</button>
              )}
              {(r.status === "archived" || r.status === "rejected" || r.status === "hidden") && (
                <button data-testid={`restore-${r.id}`} onClick={() => setStatus(r, "pending")} className="text-xs font-bold rounded px-2.5 py-1.5 bg-amber-100 text-amber-700 hover:bg-amber-200">Restore</button>
              )}
              <button data-testid={`feature-${r.id}`} onClick={() => toggleFeatured(r)} className="text-xs font-bold rounded px-2.5 py-1.5 border border-gold/50 text-navy hover:bg-gold/10">{r.featured ? "Unfeature" : "Feature"}</button>
            </div>

            {respondingId === r.id ? (
              <div className="mt-3">
                <textarea data-testid={`response-input-${r.id}`} rows={2} value={responseText} onChange={(e) => setResponseText(e.target.value)} placeholder="Write a public reply..." className="w-full rounded-md border border-navy/20 px-3 py-2 text-sm outline-none focus:border-brick resize-none" />
                <div className="flex gap-2 mt-2">
                  <button data-testid={`save-response-${r.id}`} onClick={() => submitResponse(r)} className="text-xs font-bold bg-brick text-white rounded px-3 py-1.5">Save reply</button>
                  <button onClick={() => { setRespondingId(null); setResponseText(""); }} className="text-xs font-bold text-navy/60">Cancel</button>
                </div>
              </div>
            ) : (
              <button data-testid={`respond-btn-${r.id}`} onClick={() => { setRespondingId(r.id); setResponseText(r.owner_response || ""); }} className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-navy hover:text-brick">
                <MessageSquareReply className="w-3.5 h-3.5" /> {r.owner_response ? "Edit reply" : "Reply publicly"}
              </button>
            )}
          </div>
        ))}
        {visible.length === 0 && <p className="text-navy/50">No reviews in this view.</p>}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-auto" onClick={() => setOpen(false)}>
          <form data-testid="review-form" onClick={(e) => e.stopPropagation()} onSubmit={save} className="bg-white rounded-lg w-full max-w-lg p-6 my-8">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy">{editing ? "Edit Review" : "Add Review"}</h2>
              <button type="button" onClick={() => setOpen(false)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>

            <label className="block text-sm font-bold text-navy uppercase mt-4">Customer name</label>
            <input data-testid="rf-author" required value={form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

            <label className="block text-sm font-bold text-navy uppercase mt-4">Title (optional)</label>
            <input data-testid="rf-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />

            <label className="block text-sm font-bold text-navy uppercase mt-4">Review text</label>
            <textarea data-testid="rf-text" required rows={4} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick resize-none" />
            {editing && <p className="text-xs text-ink/50 mt-1">Editing the text preserves the customer's original words on record.</p>}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Rating</label>
                <select data-testid="rf-rating" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick bg-white">
                  {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} stars</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Status</label>
                <select data-testid="rf-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick bg-white capitalize">
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Type</label>
                <select data-testid="rf-type" value={form.review_type} onChange={(e) => setForm({ ...form, review_type: e.target.value })} className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick bg-white">
                  <option value="store">Store</option><option value="product">Product</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Location (optional)</label>
                <input data-testid="rf-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. Jacksonville, FL" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
              </div>
            </div>

            {form.review_type === "product" && (
              <>
                <label className="block text-sm font-bold text-navy uppercase mt-4">Product ID (optional)</label>
                <input data-testid="rf-product-id" value={form.product_id} onChange={(e) => setForm({ ...form, product_id: e.target.value })} placeholder="Mongo product id" className="mt-1 w-full rounded-md border border-navy/20 px-3 py-2.5 outline-none focus:border-brick" />
              </>
            )}

            <div className="flex gap-5 mt-5">
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="rf-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured
              </label>
              <label className="flex items-center gap-2 text-sm font-semibold text-navy">
                <input data-testid="rf-verified" type="checkbox" checked={form.verified} onChange={(e) => setForm({ ...form, verified: e.target.checked })} /> Verified Buyer
              </label>
            </div>

            <button data-testid="save-review-btn" disabled={saving} className="mt-6 w-full bg-brick text-white font-bold uppercase rounded-md py-3 hover:bg-navy transition-colors disabled:opacity-60">
              {saving ? "Saving..." : editing ? "Save Changes" : "Add Review"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
